import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DOCS_ENDPOINTS } from '../../pages/docs/data/docs-api-data';

/** One search hit. Frontend-only for now docs content is moving to
 *  WordPress rather than getting a Rails search endpoint, so there's no
 *  backend contract to shape this around any more (see git history for
 *  the api-contracts/docs-search.md this used to mirror, since removed).
 *  Kept as its own type/shape anyway in case a real search integration
 *  (WordPress's own or otherwise) replaces this service later without
 *  DocsSearch needing to change. */
export interface DocsSearchResult {
  /** Everything after "/docs/" in the route: "quickstart",
   *  "api-reference/rag", "" for the docs index itself. */
  page: string;
  /** Anchor within the page, or null when the match is the page/heading
   *  itself rather than a specific subsection. */
  fragment: string | null;
  /** The specific heading that matched not necessarily the page's own
   *  title (see .breadcrumbs for how it nests under that title). */
  header: string;
  /** Plain-text snippet from around the match. No HTML/markdown. */
  content: string;
  /** Section trail down to `header`, top-level first. */
  breadcrumbs: string[];
  /** Higher = more relevant. */
  score: number;
}

/** Real page copy (subtitles/taglines pulled straight from each page's
 *  own template/data file see docs-api-data.ts for the three
 *  endpoints), not placeholder text, so this reads like the real
 *  content it stands in for rather than lorem ipsum. Scores are set at
 *  query time (see search()), not stored here. */
const INDEX: Omit<DocsSearchResult, 'score'>[] = [
  { page: '', fragment: null, header: 'Introduction', content: 'The metered retrieval API for AI, built on the Cikisi index.', breadcrumbs: ['Get started', 'Introduction'] },
  { page: 'quickstart', fragment: null, header: 'Quickstart', content: 'From nothing to a first response, in three steps.', breadcrumbs: ['Get started', 'Quickstart'] },
  { page: 'authentication', fragment: null, header: 'Authentication', content: 'Every endpoint is a POST with a JSON body, authenticated with an organization API key issued to you from the developer portal.', breadcrumbs: ['Get started', 'Authentication'] },
  { page: 'making-a-call', fragment: null, header: 'Making a call', content: 'The body is nested, not flat: parameters live inside a request or response envelope.', breadcrumbs: ['Guides', 'Making a call'] },
  { page: 'making-a-call', fragment: 'response-headers', header: 'Response headers', content: 'Every response carries a handful of headers alongside its body, covering credit cost and rate-limit state.', breadcrumbs: ['Guides', 'Making a call', 'Response headers'] },
  { page: 'pricing-limits', fragment: null, header: 'Pricing & limits', content: 'Top up your wallet. Calls are authorized against the balance.', breadcrumbs: ['Guides', 'Pricing & limits'] },
  { page: 'errors', fragment: null, header: 'Errors', content: 'Every error is the same envelope: { "error": { "code", "message", "details" } }. Switch on code, never on the message text.', breadcrumbs: ['Guides', 'Errors'] },
  { page: 'account-filters', fragment: null, header: 'Account-scoped filters', content: 'Everything else dates, domains, language, geography behaves the same either way.', breadcrumbs: ['Guides', 'Account-scoped filters'] },
  { page: 'api-reference', fragment: null, header: 'All endpoints', content: 'Every endpoint the API exposes, with request/response schemas.', breadcrumbs: ['API Reference', 'All endpoints'] },
  ...endpointIndexEntries(),
  { page: 'changelog', fragment: null, header: 'Changelog', content: "What's changed in the Mila Retrieval API.", breadcrumbs: ['Changelog'] },
];

/** The endpoint's own page, plus its four real subsections (Request
 *  body / Response body / Example / Response schemas the exact anchors
 *  DocsApiReferenceDetail renders and the sidebar already links to, see
 *  docs-nav__subitem in docs-layout.html) rather than just one entry per
 *  endpoint. Without these, searching for something that's genuinely on
 *  the page "example", "schemas" turned up nothing, which is worse
 *  than not having an index at all. Built from DOCS_ENDPOINTS rather than
 *  hand-listing rag/answer/search, so a fourth endpoint there picks up
 *  its own search entries for free the same reasoning DocsLayout's own
 *  sidebar generation already follows. Cikisi-only parameters are left
 *  out: that section only renders for orgs connected to Cikisi (see
 *  DocsApiReferenceDetail), and this index has no notion of who's asking. */
function endpointIndexEntries(): Omit<DocsSearchResult, 'score'>[] {
  return DOCS_ENDPOINTS.flatMap(ep => {
    const label = ep.id.toUpperCase();
    const page = `api-reference/${ep.id}`;
    const crumbs = (...rest: string[]) => ['API Reference', label, ...rest];
    return [
      { page, fragment: null, header: label, content: ep.summary, breadcrumbs: crumbs() },
      { page, fragment: `${ep.id}-request`, header: 'Request body', content: 'What to search, and where to look.', breadcrumbs: crumbs('Request body') },
      { page, fragment: `${ep.id}-response`, header: 'Response body', content: 'How much to return, and in what shape.', breadcrumbs: crumbs('Response body') },
      { page, fragment: `${ep.id}-example`, header: 'Example', content: 'A runnable request in cURL, JavaScript, and Python, with the live response traced underneath it.', breadcrumbs: crumbs('Example') },
      { page, fragment: `${ep.id}-schemas`, header: 'Response schemas', content: 'Documentation only: nothing validates or coerces an upstream payload, and bodies are relayed untouched.', breadcrumbs: crumbs('Response schemas') },
    ];
  });
}

@Injectable({ providedIn: 'root' })
export class DocsSearchService {
  /** Ranks entirely client-side against the index above: a hit anywhere
   *  in the header scores higher than one only in the body content, and
   *  an earlier match within that field scores higher than a later one
   *  a rough but reasonable proxy for what a real search backend's
   *  relevance score would produce, without needing one. */
  search(query: string): Observable<DocsSearchResult[]> {
    const q = query.trim().toLowerCase();
    if (!q) return of([]);

    const results: DocsSearchResult[] = [];
    for (const entry of INDEX) {
      const headerIdx = entry.header.toLowerCase().indexOf(q);
      const contentIdx = entry.content.toLowerCase().indexOf(q);
      if (headerIdx === -1 && contentIdx === -1) continue;

      const score = headerIdx !== -1
        ? 10 - headerIdx * 0.05
        : 5 - contentIdx * 0.01;
      results.push({ ...entry, score: Math.round(score * 100) / 100 });
    }
    results.sort((a, b) => b.score - a.score);

    return of(results);
  }
}
